// frontend/src/App.js

import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import posthog from 'posthog-js';
import initSentry from './sentry';
import analytics from './telemetry/analytics';
// import indexedDBStorage from './storage/indexedDB'; // Temporarily disabled for build fix
import eventBus, { Events } from './core/eventBus';

// ---- EAGER imports: primary routes + react-snap pre-rendered pages ----
// These stay in the main bundle to avoid hydration mismatches and spinners
// on the core user path.
import Signup from './components/Auth/Signup';
import Login from './components/Auth/Login';
import Landing from './pages/Landing';
import VoiceLanding from './pages/VoiceLanding';
import Pricing from './pages/Pricing';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
// Sessions + Bookmarks lazy-loaded: they import heavy deps (react-markdown, SessionGraph, Tiptap)
// that bloat the main bundle. /chats is fast because it's lazy — same pattern here.
import SharedProject from './pages/SharedProject';
import SharedSession from './pages/SharedSession';
import SharedVoice from './pages/SharedVoice';
import PublicProfile from './pages/PublicProfile';
import CookiePolicy from './pages/CookiePolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Support from './pages/Support';
import Explore from './pages/Explore';
import JobSearch from './pages/usecases/JobSearch';
import Research from './pages/usecases/Research';
import ProjectSwitch from './pages/usecases/ProjectSwitch';
import ExtensionAuth from './pages/ExtensionAuth';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PROJECTS_ENABLED } from './config/featureFlags';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './hooks/useSubscription';
import { ExtensionSubscriptionProvider } from './hooks/useExtensionSubscription';
import { RecordingProvider } from './contexts/SimpleRecordingContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagProvider';
import RecordingDock from './components/RecordingDock';
import PrivateRoute from './components/Auth/PrivateRoute';
import AppShell from './components/AppShell';
import TestConnection from './components/TestConnection';
import TodayRedirect from './components/TodayRedirect';
import UsageNotificationSnackbar from './components/UsageNotificationSnackbar';
import { VoiceRecovery } from './components/VoiceRecovery';
import LegalAcceptanceModal from './components/LegalAcceptanceModal';
import UpgradePromptModal from './components/UpgradePromptModal';

// ---- LAZY imports: authenticated secondary routes ----
// These pull heavyweight deps (graphology, react-force-graph-2d, Tiptap,
// recharts, react-markdown, Stripe) into separate chunks loaded on demand.
// Must be after all import statements (ESLint import/first rule).
const Sessions = lazy(() => import('./features/sessions/Sessions'));
const Bookmarks = lazy(() => import('./features/bookmarks/Bookmarks'));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const AIWorkspace = lazy(() => import('./components/AIWorkspace'));
const AddNote = lazy(() => import('./components/AddNote'));
const ImportPage = lazy(() => import('./features/import/pages/ImportPage'));
const Voices = lazy(() => import('./pages/Voices'));
const ProjectPage = lazy(() => import('./pages/ProjectPage'));
const ProjectsListPage = lazy(() => import('./pages/ProjectsListPage'));
const Settings = lazy(() => import('./pages/Settings'));
const TrashView = lazy(() => import('./pages/TrashView'));
const NoteEditorDemo = lazy(() => import('./pages/NoteEditorDemo'));
const Help = lazy(() => import('./pages/Help'));
const Upgrade = lazy(() => import('./pages/Upgrade'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const CheckoutCancel = lazy(() => import('./pages/CheckoutCancel'));
const QuickVoice = lazy(() => import('./pages/QuickVoice'));
const Admin = lazy(() => import('./pages/Admin'));
const ActivityPage = lazy(() => import('./pages/ActivityPage'));
const StarredSessions = lazy(() => import('./pages/StarredSessions'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const FollowingPage = lazy(() => import('./pages/FollowingPage'));
const AiChats = lazy(() => import('./pages/AiChats'));
const Ask = lazy(() => import('./pages/Ask'));

// Component to track page views and route changes
function RouteTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page views with PostHog
    try {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        path: location.pathname
      });
    } catch (error) {
      console.warn('Failed to track page view:', error);
    }

    // Track page views with Google Analytics
    if (window.gtag) {
      window.gtag('config', 'G-YNP2E4V67G', {
        page_path: location.pathname + location.search
      });
    }

    // Track page in analytics and emit event
    const pageName = location.pathname.replace('/', '') || 'dashboard';
    analytics.page(pageName, {
      full_path: location.pathname,
      search: location.search
    });

    eventBus.emit(Events.PAGE_CHANGE, {
      path: location.pathname,
      pageName
    });

  }, [location]);
  
  return null;
}

// Component to handle conditional rendering based on auth status
function AppContent() {
  const location = useLocation();
  const { currentUser, loading } = useAuth();

  // PR 4 (iOS-app-separation): capture ?source= on first load and persist.
  // Backend ensureUserRecord consumes it on first authed call. Idempotent —
  // re-runs are safe; only writes when a fresh ?source= is on the URL.
  React.useEffect(() => {
    const { captureFunnelFromUrl } = require('./utils/funnelTracker');
    captureFunnelFromUrl();
  }, []);

  // Normalize trailing slashes so Railway's /path → /path/ 301 redirect doesn't
  // misclassify public routes as protected and flip to <Landing />.
  const pathname = location.pathname.replace(/\/+$/, '') || '/';

  const isAuthPage = ['/login', '/signup'].includes(pathname);
  const isRootPath = pathname === '/';
  const isSharedPage = pathname.startsWith('/shared/') || pathname.startsWith('/u/') || /^\/@[^/]+\/[^/]+$/.test(pathname) || pathname === '/explore';
  const isLegalPage = ['/cookie-policy', '/privacy-policy', '/terms', '/support'].includes(pathname);
  const isUseCasePage = ['/use-cases/job-search', '/use-cases/research', '/use-cases/project-switch'].includes(pathname);
  const isExtensionAuthPage = pathname === '/extension-auth';
  const isAuthBridgePage = pathname === '/auth/bridge';
  // PR 4: public Voice marketing landing — visible to unauth visitors.
  const isVoiceLandingPage = pathname === '/voice-landing';
  // Public marketing pages — pricing + blog exist FOR logged-out visitors.
  // Every new public route MUST be added here AND to the flash-guard isPublic
  // list in public/index.html (mirrored in authFlashGuard.test.js).
  const isMarketingPage = pathname === '/pricing' || pathname === '/blog' || pathname.startsWith('/blog/');

  // Show loading state while checking auth — but NOT on pre-rendered public pages.
  // react-snap pre-renders / (Landing), /explore, and legal pages with loading=false.
  // If we render a spinner during hydration on those pages, React gets a mismatch
  // and bails out, leaving all onClick handlers dead.
  const isPublicPrerenderedPage = isRootPath || isSharedPage || isLegalPage || isUseCasePage || isMarketingPage;

  if (loading && !isPublicPrerenderedPage) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default'
      }}>
        Loading...
      </Box>
    );
  }

  // If user is authenticated and on root path, redirect based on funnel.
  // PR 4 (iOS-app-separation): if the user came in via /?source=voice|browser|chats,
  // land them in the matching module. Falls back to /sessions for the default flow.
  if (currentUser && isRootPath) {
    const { getPersistedFunnel, getLandingRouteForFunnel } = require('./utils/funnelTracker');
    const funnel = getPersistedFunnel();
    const target = getLandingRouteForFunnel(funnel) || '/sessions';
    return <Navigate to={target} replace />;
  }

  // If user is not authenticated and not on auth pages, shared pages, legal pages, or extension auth, show landing
  if (!currentUser && !isAuthPage && !isSharedPage && !isLegalPage && !isUseCasePage && !isExtensionAuthPage && !isAuthBridgePage && !isVoiceLandingPage && !isMarketingPage) {
    return <Landing />;
  }

  // /@username/slug → render SharedSession directly (outside Routes).
  // React Router v6 can't match /@:param (@ before param breaks matching),
  // and /:param/:slug would steal all two-segment paths like /sessions/123.
  if (/^\/@[^/]+\/[^/]+$/.test(pathname)) {
    return <SharedSession />;
  }

  // Auth bridge — signs in via Firebase custom token, then redirects (iOS "Buy on web" flow)
  if (isAuthBridgePage) {
    const AuthBridge = React.lazy(() => import('./pages/AuthBridge'));
    return (
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
        <AuthBridge />
      </React.Suspense>
    );
  }

  // Auth pages for unauthenticated users
  if (isAuthPage) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Routes>
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />
        </Routes>
      </Box>
    );
  }

  // All routes — Suspense wraps lazy-loaded authenticated components
  const LazyFallback = (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress size={32} />
    </Box>
  );

  return (
    <Suspense fallback={LazyFallback}>
    <Routes>
      {/* Public routes - no auth required, no AppShell */}
      <Route
        path="/shared/project/:token/:slug?"
        element={<SharedProject />}
      />
      <Route
        path="/shared/voice/:token"
        element={<SharedVoice />}
      />
      <Route
        path="/shared/session/:token/:slug?"
        element={<SharedSession />}
      />
      {/* /@username/slug handled above AppContent return, not here —
          React Router v6 can't match /@:param and /:param/:slug steals all 2-segment paths */}
      <Route
        path="/u/:username"
        element={<PublicProfile />}
      />
      <Route
        path="/explore"
        element={<Explore />}
      />

      {/* PR 4 (iOS-app-separation): public landing for Blackmount Voice marketing.
          Lives at /voice-landing (not /voice) because the authenticated /voice
          route already renders <Voices /> for signed-in users. The unauth check
          earlier in AppContent ensures non-logged-in visitors hit this page. */}
      <Route
        path="/voice-landing"
        element={<VoiceLanding />}
      />

      {/* Pricing — public route, accessible to both signed-in and unauth visitors.
          Standalone (no AppShell) so guests can browse without triggering the
          auth redirect chain. Signed-in users get the same page but the CTA
          wires to Stripe checkout via /api/billing/create-checkout. */}
      <Route
        path="/pricing"
        element={<Pricing />}
      />

      {/* Blog — public routes for SEO/marketing content. Index at /blog,
          individual posts at /blog/:slug. First post: pocket-alternatives-2026. */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      {/* Legal pages - public, no auth required */}
      <Route
        path="/cookie-policy"
        element={<CookiePolicy />}
      />
      <Route
        path="/privacy-policy"
        element={<PrivacyPolicy />}
      />
      <Route
        path="/terms"
        element={<TermsOfService />}
      />
      <Route
        path="/support"
        element={<Support />}
      />

      {/* Use-case pages - public SEO/marketing pages */}
      <Route
        path="/use-cases/job-search"
        element={<JobSearch />}
      />
      <Route
        path="/use-cases/research"
        element={<Research />}
      />
      <Route
        path="/use-cases/project-switch"
        element={<ProjectSwitch />}
      />

      {/* Quick Voice - extension route (no AppShell for clean UI) */}
      <Route
        path="/quick-voice"
        element={<QuickVoice />}
      />

      {/* Extension OAuth Pairing - clean UI without AppShell */}
      <Route
        path="/extension-auth"
        element={<ExtensionAuth />}
      />

      {/* All other routes use AppShell */}
      <Route
        path="*"
        element={
          <AppShell>
            <Routes>
        {/* Protected routes with AppShell */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/upgrade"
          element={
            <PrivateRoute>
              <Upgrade />
            </PrivateRoute>
          }
        />

        <Route
          path="/billing/success"
          element={
            <PrivateRoute>
              <CheckoutSuccess />
            </PrivateRoute>
          }
        />

        <Route
          path="/billing/cancel"
          element={
            <PrivateRoute>
              <CheckoutCancel />
            </PrivateRoute>
          }
        />


        <Route
          path="/today"
          element={
            <PrivateRoute>
              <TodayRedirect />
            </PrivateRoute>
          }
        />

        <Route
          path="/activity"
          element={
            <PrivateRoute>
              <ActivityPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/insights"
          element={
            <PrivateRoute>
              <InsightsPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/inbox"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/reflections"
          element={
            <PrivateRoute>
              <Box sx={{ p: 3 }}>
                <h2>Reflections</h2>
                <p>Coming soon...</p>
              </Box>
            </PrivateRoute>
          }
        />

        <Route
          path="/voices"
          element={
            <PrivateRoute>
              <Voices />
            </PrivateRoute>
          }
        />

        {/* PR 4 (iOS-app-separation): /voice alias for /voices —
            matches Blackmount Voice marketing surface. Same component. */}
        <Route
          path="/voice"
          element={
            <PrivateRoute>
              <Voices />
            </PrivateRoute>
          }
        />

        {/* PR 4: /browser alias for /sessions — Blackmount Browser surface. */}
        <Route
          path="/browser"
          element={
            <PrivateRoute>
              <Sessions />
            </PrivateRoute>
          }
        />

        <Route
          path="/bookmarks"
          element={
            <PrivateRoute>
              <Bookmarks />
            </PrivateRoute>
          }
        />

        <Route
          path="/imports"
          element={
            <PrivateRoute>
              <ImportPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/sessions"
          element={
            <PrivateRoute>
              <Sessions />
            </PrivateRoute>
          }
        />

        <Route
          path="/sessions/:id"
          element={
            <PrivateRoute>
              <Sessions />
            </PrivateRoute>
          }
        />

        <Route
          path="/chats"
          element={
            <PrivateRoute>
              <AiChats />
            </PrivateRoute>
          }
        />

        <Route
          path="/ask"
          element={
            <PrivateRoute>
              <Ask />
            </PrivateRoute>
          }
        />

        {PROJECTS_ENABLED && (
          <>
            <Route
              path="/projects"
              element={
                <PrivateRoute>
                  <ProjectsListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <PrivateRoute>
                  <ProjectPage />
                </PrivateRoute>
              }
            />
          </>
        )}

        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          }
        />

        <Route
          path="/starred"
          element={
            <PrivateRoute>
              <StarredSessions />
            </PrivateRoute>
          }
        />

        <Route
          path="/following"
          element={
            <PrivateRoute>
              <FollowingPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/help"
          element={
            <PrivateRoute>
              <Help />
            </PrivateRoute>
          }
        />

        <Route
          path="/trash"
          element={
            <PrivateRoute>
              <TrashView />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <PrivateRoute>
              <Admin />
            </PrivateRoute>
          }
        />

        <Route
          path="/ai-workspace"
          element={
            <PrivateRoute>
              <AIWorkspace />
            </PrivateRoute>
          }
        />

        <Route
          path="/note-editor-demo"
          element={
            <PrivateRoute>
              <NoteEditorDemo />
            </PrivateRoute>
          }
        />

        <Route
          path="/notes/new"
          element={
            <PrivateRoute>
              <AddNote />
            </PrivateRoute>
          }
        />

        <Route
          path="/test"
          element={
            <PrivateRoute>
              <TestConnection />
            </PrivateRoute>
          }
        />

        {/* Redirect root path to dashboard */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
            </Routes>
          </AppShell>
        }
      />
    </Routes>
    </Suspense>
  );
}

function App() {
  const [legalAcceptanceRequired, setLegalAcceptanceRequired] = React.useState(false);
  const [legalVersionDetails, setLegalVersionDetails] = React.useState(null);

  // Initialize all systems on app startup
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // 1. Initialize Sentry for error tracking
        initSentry();
        
        // 2. Initialize PostHog
        const posthogKey = process.env.REACT_APP_POSTHOG_KEY || 'phc_t51RmkGezpY61mhpq2l1jH97GcxHOM8aZnp28gSdYyF';
        const posthogHost = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
        
        if (posthogKey && posthogHost) {
          posthog.init(posthogKey, {
            api_host: posthogHost,
            capture_pageview: false, // We handle this manually in RouteTracker
            persistence: 'localStorage+cookie',
            loaded: (posthog) => {
              // PostHog initialized
            }
          });
        } else {
          console.warn('[PostHog] Missing environment variables - analytics disabled');
          console.warn('Expected: REACT_APP_POSTHOG_KEY and REACT_APP_POSTHOG_HOST');
        }

        // 3. Initialize analytics system
        analytics.init();

        // 4. Initialize IndexedDB storage (temporarily disabled for build fix)
        // await indexedDBStorage.init();

        // 5. Set up periodic cleanup (run every 24 hours)
        // const cleanupInterval = setInterval(() => {
        //   indexedDBStorage.cleanup();
        // }, 24 * 60 * 60 * 1000);

        // 6. Set up global error handling with PostHog
        window.addEventListener('error', (event) => {
          const error = event.error || new Error(event.message);
          const stack_first_line = error.stack ? error.stack.split('\n')[0] : '';
          
          // Send to PostHog (free error tracking)
          try {
            posthog.capture('js_error', {
              name: error.name,
              message: error.message,
              file: event.filename,
              line: event.lineno,
              col: event.colno,
              stack_first_line
            });
          } catch (err) {
            console.warn('Failed to capture error in PostHog:', err);
          }
          
          // Also send through analytics for development logging
          analytics.error(error, {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
          });
        });

        window.addEventListener('unhandledrejection', (event) => {
          const reason = event.reason;
          const stack_first_line = reason?.stack ? reason.stack.split('\n')[0] : '';
          
          // Send to PostHog
          try {
            posthog.capture('js_error', {
              name: 'UnhandledPromiseRejection',
              message: String(reason),
              stack_first_line,
              type: 'unhandled_promise_rejection'
            });
          } catch (err) {
            console.warn('Failed to capture promise rejection in PostHog:', err);
          }
          
          analytics.error(reason, {
            type: 'unhandled_promise_rejection'
          });
        });


        // Cleanup on unmount
        // return () => {
        //   clearInterval(cleanupInterval);
        // };

      } catch (error) {
        console.error('[App] Failed to initialize systems:', error);
        // Still continue with app loading even if analytics fails
      }
    };

    initializeApp();
  }, []);

  // Listen for legal acceptance required events from axios interceptor
  useEffect(() => {
    const handleLegalAcceptanceRequired = (event) => {
      setLegalVersionDetails(event.detail);
      setLegalAcceptanceRequired(true);
    };

    window.addEventListener('legal-acceptance-required', handleLegalAcceptanceRequired);

    return () => {
      window.removeEventListener('legal-acceptance-required', handleLegalAcceptanceRequired);
    };
  }, []);

  const handleLegalAccepted = () => {
    setLegalAcceptanceRequired(false);
    setLegalVersionDetails(null);
    // Clear multi-tab coordination flag
    localStorage.removeItem('legalModalOpen');
    // Refresh the page to reload user data with updated legal acceptance
    window.location.reload();
  };

  return (
    <HelmetProvider>
      <ThemeContextProvider>
        <Router>
          <AuthProvider>
            <SubscriptionProvider>
              <ExtensionSubscriptionProvider>
                <FeatureFlagProvider>
                  <RecordingProvider>
                    <RouteTracker />
                    <RecordingDock />
                    <UsageNotificationSnackbar />
                    <LegalAcceptanceModal
                      open={legalAcceptanceRequired}
                      versionDetails={legalVersionDetails}
                      onAccepted={handleLegalAccepted}
                    />
                    <UpgradePromptModal />
                    <AppContent />
                  </RecordingProvider>
                </FeatureFlagProvider>
              </ExtensionSubscriptionProvider>
            </SubscriptionProvider>
          </AuthProvider>
        </Router>
      </ThemeContextProvider>
    </HelmetProvider>
  );
}

export default App;
